import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from 'react-query';
import { useTranslation } from 'react-i18next';

import Layout from './components/Layout.tsx';
import { LanguageSwitcher } from './components/LanguageSwitcher.tsx';
import Dashboard from './pages/Dashboard.tsx';
import History from './pages/History.tsx';
import Reports from './pages/Reports.tsx';
import DeviceDetails from './pages/DeviceDetails.tsx';
import SessionDetails from './pages/SessionDetails.tsx';
import SessionResults from './pages/SessionResults.tsx';
import Login from './pages/Login.tsx';
import { AdminProvider } from './contexts/PostureContext.tsx';
import { AuthProvider, useAuth } from './contexts/AuthContext.tsx';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function ProtectedLayout() {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();

  if (isLoading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center bg-regina-gray-50">
        <div className="fixed top-3 right-3 z-50">
          <LanguageSwitcher />
        </div>
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-regina-red mx-auto" />
          <p className="text-regina-gray-600 mt-4 text-sm">{t('app.loadingAuth')}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return (
    <AdminProvider>
      <Layout>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/history" element={<History />} />
          <Route path="/reports" element={<Reports />} />
          <Route path="/device/:deviceId" element={<DeviceDetails />} />
          <Route path="/session/:sessionId" element={<SessionDetails />} />
        </Routes>
      </Layout>
    </AdminProvider>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <div className="min-h-screen bg-regina-gray-50">
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/results/:reportId" element={<SessionResults />} />
              <Route path="/*" element={<ProtectedLayout />} />
            </Routes>
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#1e293b',
                  color: '#f1f5f9',
                  border: '1px solid #334155',
                },
              }}
            />
          </div>
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
